import { useCallback, useEffect, useState } from 'react';

/**
 * Detecta iPhone/iPad rodando fora do modo standalone — público-alvo
 * do onboarding "Adicionar à Tela de Início". Safari iOS não emite
 * `beforeinstallprompt`, então sem essa instrução o usuário fica preso:
 * o toggle de Web Push no /perfil exige PWA instalada, mas o caminho
 * de instalação está escondido atrás do botão Compartilhar do Safari.
 *
 * Estado:
 *  - shouldAutoShow : true para mostrar o sheet automaticamente
 *  - canShowHint    : true para botão manual ("Como instalar?")
 *
 * Dismissal persiste em localStorage:
 *  - 'remind-later'  → TTL 3 dias
 *  - 'installed'     → permanente (até cache local ser limpo)
 */

const DISMISS_KEY = 'rx-ios-install-dismissed';

interface DismissRecord {
  mode: 'remind-later' | 'installed';
  at: number;
}

const REMIND_TTL_MS = 3 * 24 * 60 * 60 * 1_000;

function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ se identifica como Macintosh — diferencia pelo touch.
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return true;
  return false;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  return (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

function readDismiss(): DismissRecord | null {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DismissRecord;
    if (parsed.mode !== 'remind-later' && parsed.mode !== 'installed') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeDismiss(mode: DismissRecord['mode']): void {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify({ mode, at: Date.now() }));
  } catch {
    /* tolerante */
  }
}

function isDismissActive(record: DismissRecord | null): boolean {
  if (!record) return false;
  if (record.mode === 'installed') return true;
  return Date.now() - record.at < REMIND_TTL_MS;
}

export interface IosInstallHint {
  /** Auto-show no Dashboard — true só se iOS, fora de standalone, sem dismissal ativo. */
  shouldAutoShow: boolean;
  /** Cabe mostrar botão manual ("Como instalar?") — iOS fora de standalone, ignora dismissal. */
  canShowHint: boolean;
  /** iOS detectado, independente de standalone. */
  isIos: boolean;
  dismiss(mode: 'remind-later' | 'installed'): void;
}

export function useIosInstallHint(): IosInstallHint {
  const [isIos] = useState<boolean>(isIosDevice());
  const [standalone, setStandalone] = useState<boolean>(isStandalone());
  const [dismissActive, setDismissActive] = useState<boolean>(() => isDismissActive(readDismiss()));

  useEffect(() => {
    // Se o usuário instalar enquanto a aba está aberta (caso raro mas possível
    // em pwa-detector via media query), reconhecemos sem reload.
    const mq = window.matchMedia?.('(display-mode: standalone)');
    if (!mq) return;
    const handle = (): void => setStandalone(mq.matches);
    mq.addEventListener?.('change', handle);
    return () => {
      mq.removeEventListener?.('change', handle);
    };
  }, []);

  const dismiss = useCallback((mode: 'remind-later' | 'installed') => {
    writeDismiss(mode);
    setDismissActive(true);
  }, []);

  const canShowHint = isIos && !standalone;
  const shouldAutoShow = canShowHint && !dismissActive;

  return { shouldAutoShow, canShowHint, isIos, dismiss };
}
